import Config from '@config';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios, { CanceledError, isCancel } from 'axios';

import MLALogger from './logger';

export interface ApiResponse<T = any> {
    data: T;
    status: number;
    message?: string;
}

const axiosApi = axios.create({
    baseURL: Config.BASE_GRAPHQL_SERVER_URL,
});

let _getToken: (() => Promise<string>) | null = null;

export const setTokenProvider = (fn: () => Promise<string>) => {
    _getToken = fn;
};

export function isAxiosCanceledError(error: unknown): boolean {
    if (isCancel(error)) return true;
    const err = error as { code?: string; name?: string };
    return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
}

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

axiosApi.interceptors.request.use(async (config) => {
    if (_getToken) {
        try {
            const token = await _getToken();
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
            console.error('Failed to get access token:', error);
        }
    }
    return config;
});

axiosApi.interceptors.response.use(
    function onFulfilled(response) {
        const code = response?.data?.errors?.[0]?.extensions?.data?.Code;
        if (code === '1040' || code === '1050') {
            return Promise.reject(new Error(`error code ${code}: ${response?.data?.errors?.[0]?.message}`));
        }
        return response;
    },
    function onRejected(error: AxiosError | unknown) {
        if (isAxiosCanceledError(error)) {
            const cfg = axios.isAxiosError(error)
                ? (error.config as InternalAxiosRequestConfig | undefined)
                : undefined;
            MLALogger.Log(
                ['axios-client', 'request-cancelled'],
                cfg
                    ? {
                          method: (cfg.method ?? 'get').toUpperCase(),
                          url:
                              (cfg.url?.startsWith('http') ? cfg.url : `${cfg.baseURL ?? ''}${cfg.url ?? ''}`) ||
                              undefined,
                      }
                    : { message: String(error) },
                'warn'
            );
        }
        return Promise.reject(error);
    }
);

// ---------------------------------------------------------------------------
// Retry / timeout
// ---------------------------------------------------------------------------

const BACKOFF_BASE_MS = 300;
const BACKOFF_MAX_MS = 5000;

/**
 * Whether a failed axios request is safe to retry.
 */
export function shouldRetryAxiosError(error: unknown): boolean {
    if (isAxiosCanceledError(error)) return false;
    // 1040 (auth) and 1050 (custom) are app-level GraphQL errors thrown by the response interceptor — never retried.
    if (error instanceof Error && /^error code (1040|1050):/.test(error.message)) return false;
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status != null) return status >= 500 || status === 429;
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;
        if (error.request && error.response === undefined) return true;
    }
    return false;
}

function getBackoffDelayMs(error: unknown, failureIndex: number): number {
    if (axios.isAxiosError(error) && error.response?.status === 429) {
        const sec = parseInt(String(error.response.headers?.['retry-after']), 10);
        if (!Number.isNaN(sec) && sec >= 0) return sec * 1000;
    }
    return Math.floor(Math.random() * Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * Math.pow(2, failureIndex)));
}

/**
 * Cancel-aware delay. Resolves after `ms` milliseconds, or rejects with
 * `CanceledError` immediately if `signal` is already aborted or fires during the wait.
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return Promise.reject(new CanceledError());
    return new Promise((resolve, reject) => {
        const onAbort = () => {
            clearTimeout(tid);
            reject(new CanceledError());
        };
        const tid = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

export interface RetryRequestOptions {
    retryNumber?: number;
    timeout?: number;
    signal?: AbortSignal;
}

/**
 * Execute `requestFn` with bounded retries, per-attempt timeout, and exponential backoff.
 */
export async function axiosRequestWithRetry<T>(
    requestFn: (config: { timeout?: number; signal?: AbortSignal }) => Promise<T>,
    options?: RetryRequestOptions
): Promise<T> {
    const { retryNumber = 0, timeout = 0, signal } = options ?? {};
    const maxAttempts = Math.max(0, retryNumber) + 1;
    const config = { ...(timeout > 0 && { timeout }), ...(signal && { signal }) };
    let failures = 0;

    while (true) {
        try {
            return await requestFn(config);
        } catch (error) {
            failures += 1;
            const retryable = shouldRetryAxiosError(error);
            if (!retryable || failures >= maxAttempts) {
                if (retryable) {
                    MLALogger.Log(
                        ['axios-client', 'retry-give-up'],
                        {
                            failureCount: failures,
                            maxAttempts,
                            lastError: error instanceof Error ? error.message : String(error),
                        },
                        'warn'
                    );
                }
                throw error;
            }
            const delayMs = getBackoffDelayMs(error, failures);
            MLALogger.Log(
                ['axios-client', 'retry-scheduled'],
                { failureCount: failures, maxAttempts, delayMs },
                'warn'
            );
            await sleep(delayMs, signal);
        }
    }
}

export default axiosApi;

export { CanceledError, isCancel as isAxiosCancel } from 'axios';
